import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useCart } from "@/context/CartContext";
import useSEO from "@/lib/useSEO";
import useCharity from "@/lib/useCharity";
import Reveal from "@/components/Reveal";

export default function Checkout() {
  useSEO("Checkout | Fleece The Regime", "Secure checkout — Stripe payments.", true);
  const { items, subtotal } = useCart();
  const [placing, setPlacing] = useState(false);
  const [pause, setPause] = useState(null);
  const charity = useCharity();

  useEffect(() => {
    api.get("/checkout/status").then((r) => setPause(r.data)).catch(() => {});
  }, []);

  const itemCount = items.reduce((n, i) => n + i.qty, 0);
  const shipping = (subtotal >= 65 ? 0 : 4.95) + 1.95 * Math.max(0, itemCount - 1);
  const total = subtotal + shipping;

  const payWithCard = async () => {
    setPlacing(true);
    items.forEach((i) => track("checkout_start", { slug: i.slug, color: i.color }));
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.productId, name: i.name, color: i.color, size: i.size, qty: i.qty, price: i.price })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <main data-testid="checkout-empty" className="pt-16 min-h-screen flex items-center">
        <div className="px-6 sm:px-12 lg:px-20 py-24">
          <h1 className="font-display uppercase text-6xl sm:text-7xl leading-[0.85] tracking-tighter">
            Your cart is<br />suspiciously<br /><span className="text-primary">empty.</span>
          </h1>
          <Link
            to="/shop"
            data-testid="empty-shop-link"
            className="mt-10 inline-block border-2 border-black bg-primary px-8 py-4 font-display uppercase text-xl text-white shadow-hard btn-press hover:bg-black"
          >
            Fix that
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="pt-16 min-h-screen">
      <div className="grid lg:grid-cols-2">
        <div className="px-6 sm:px-12 py-14 lg:py-20 lg:border-r-2 border-black">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">Checkout</p>
            <h1 className="font-display uppercase text-5xl sm:text-6xl leading-[0.85] tracking-tighter" data-testid="checkout-heading">
              Almost<br />yours.
            </h1>
            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-3 rounded-lg border border-black/15 bg-white p-4" data-testid="stripe-bridge-card">
                <Lock size={16} className="mt-0.5 shrink-0 text-primary" />
                <p className="font-mono text-xs text-black/60 leading-relaxed">
                  One secure page does it all: <strong className="text-black">Stripe collects your email, US shipping
                  address, and payment together</strong> — Apple Pay and Google Pay fill their saved
                  address automatically. Tax is calculated from your address and shown before you confirm (if no tax
                  applies, you'll see $0.00). Your card details never touch our servers. Paid orders wait for the
                  owner's personal review before anything prints.
                </p>
              </div>
              {pause?.paused && (
                <div className="rounded-lg border-2 border-black bg-accent p-4" data-testid="checkout-paused-banner">
                  <p className="font-mono text-xs font-bold uppercase text-black">Checkout is paused right now</p>
                  <p className="font-mono text-xs text-black/70 mt-1">
                    {pause.message || "The owner has briefly paused new orders — the catalog stays up, please try again shortly."}
                  </p>
                </div>
              )}
              <button
                onClick={payWithCard}
                disabled={placing || pause?.paused}
                data-testid="checkout-pay-button"
                className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-black bg-primary py-5 font-display uppercase text-2xl text-white shadow-hard btn-press hover:bg-black disabled:opacity-50"
              >
                <Lock size={20} />
                {placing ? "Redirecting to Stripe…" : pause?.paused ? "Checkout paused" : "Continue to secure checkout"}
              </button>
              <p className="font-mono text-[11px] text-black/50 text-center" data-testid="checkout-methods-note">
                All major cards · Apple Pay · Google Pay · Affirm · Klarna · Cash App Pay
              </p>
            </div>
          </Reveal>
        </div>

        <div className="px-6 sm:px-12 py-14 lg:py-20 bg-[#EAEAE4]">
          <h2 className="font-display uppercase text-3xl tracking-tight" data-testid="summary-heading">The damage</h2>
          <div className="mt-8 space-y-4">
            {items.map((i) => (
              <div key={i.key} data-testid={`summary-item-${i.key}`} className="flex justify-between gap-4 border-b-2 border-black/10 pb-4 font-mono text-sm">
                <span>
                  {i.name} <span className="text-black/50">· {i.color} / {i.size} × {i.qty}</span>
                </span>
                <span className="font-bold">${(i.price * i.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 space-y-2 font-mono text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between">
              <span>Shipping</span>
              <span>{shipping === 0 ? "FREE ($65+)" : `$${shipping.toFixed(2)}`}</span>
            </div>
            {itemCount > 1 && (
              <p className="font-mono text-[10px] uppercase tracking-wider text-black/50 text-right" data-testid="multi-item-shipping-note">
                +$1.95 per extra item — may ship as separate tracked parcels
              </p>
            )}
            <div className="flex justify-between border-t-2 border-black pt-3 text-lg font-bold" data-testid="summary-total">
              <span>Estimated total before tax</span><span>${total.toFixed(2)}</span>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-black/50 text-right" data-testid="tax-note">
              Sales tax calculated from your address on the next page — shown before you confirm
            </p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3" data-testid="trust-badges">
            {[
              ["Secure checkout", "Card details never touch our servers — Stripe handles everything (PCI Level 1)."],
              ["Apple Pay & Google Pay", "One-tap express payment with your saved address on supported devices."],
              ["30-day defect cover", "Misprints or defects: free replacement or refund within 30 days of delivery. Wrong size? Email us within 30 days — reviewed case by case."],
              ["Printed to order, USA", "Production 2–7 business days, then tracked US delivery in about 2–5. Shipping: $4.95 first item + $1.95 per extra; first item ships free on orders $65+ (separate tracked parcels)."],
            ].map(([t, d]) => (
              <div key={t} className="border-2 border-black bg-background p-3">
                <p className="font-mono text-xs font-bold uppercase tracking-widest">{t}</p>
                <p className="mt-1 font-mono text-[11px] leading-snug text-black/60">{d}</p>
              </div>
            ))}
            {charity && (
              <div className="col-span-2 border-2 border-black bg-background p-3" data-testid="charity-badge">
                <p className="flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-widest">
                  <Heart size={13} className="text-primary" /> Our give-back: 5% of annual net profits
                </p>
                <p className="mt-1 font-mono text-[11px] leading-snug text-black/60">
                  {charity.blurb} We donate 5% of annual net profits; {charity.name} is the current recipient.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
